import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  DollarSign,
  ScanLine,
  ShieldCheck,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { DocPageThumb } from '@/components/DocPagePreview';
import { PdfThumb } from '@/components/PdfThumb';

import { useNotice } from '@/components/ConfirmDialog';
import { DatePickerSheet } from '@/components/DatePickerSheet';
import { StickyBottomBar } from '@/components/StickyBottomBar';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { ExpenseRecurring } from '@/db/types';
import { parseLocalDate, toLocalYMD } from '@/lib/dates';
import { addDocument } from '@/lib/documents';
import { persistPickedFile } from '@/lib/files';
import {
  allExpenseCategories,
  RECURRING_OPTIONS,
  addExpense,
} from '@/lib/expenses';
import { goBackOrTo } from '@/lib/nav';
import {
  classifyPaidStatus,
  composeScanNotes,
  composeScanTitle,
  scanReceiptBase64,
  type ParsedReceipt,
} from '@/lib/ocr';
import { pickAndScanFile } from '@/lib/scan-intake';
import { takeSmartScan } from '@/lib/smart-scan';
import { getActiveProperty } from '@/lib/tasks';
import { guessExpenseCategory } from '@/lib/vendor-category';
import { webNoOutline } from '@/lib/web-style';

export default function NewExpense() {
  const router = useRouter();
  const db = useSQLiteContext();
  // Wave 61: user-defined categories join the picker. Loaded once from
  // the active property row.
  const [customCatsJson, setCustomCatsJson] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      const p = await getActiveProperty(db);
      setCustomCatsJson(p?.custom_expense_categories_json ?? null);
    })();
  }, [db]);
  const notice = useNotice();

  const [category, setCategory] = useState<string>('Utilities');
  // Tracks whether the user has explicitly picked a category. We only let
  // the receipt scan overwrite the default ('Utilities') — never an
  // intentional pick.
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [vendor, setVendor] = useState('');
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(toLocalYMD(Date.now()));
  const [description, setDescription] = useState('');
  const [recurring, setRecurring] = useState<ExpenseRecurring | null>(null);
  const [taxDeductible, setTaxDeductible] = useState(false);
  const [notes, setNotes] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [recOpen, setRecOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanHint, setScanHint] = useState<string | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  // The scanned receipt photo (or PDF via Smart Scan), kept so save()
  // can persist it as a Document and attach it to the expense.
  const [scanUri, setScanUri] = useState<string | null>(null);
  const [scanMime, setScanMime] = useState<string>('image/jpeg');
  /** Original filename from the picker — lets the docx/text "paper
   *  page" preview identify the format when the mime is generic. */
  const [scanName, setScanName] = useState<string | null>(null);
  // 'receipt' when the scan reads as proof-of-payment (PAID / $0 due),
  // 'invoice' when it still carries due-language — drives the attached
  // document's kind so Files categorizes it the way a human would.
  const [scanDocKind, setScanDocKind] = useState<'receipt' | 'invoice'>('receipt');

  // Smart Scan hand-off (/scan classified a photo as an expense and
  // routed here) — consume once on mount, pre-fill, attach the photo.
  useEffect(() => {
    const handoff = takeSmartScan('expense');
    if (!handoff) return;
    setScanUri(handoff.uri);
    if (handoff.name) setScanName(handoff.name);
    if (handoff.mime) {
      setScanMime(handoff.mime);
      // A PDF handed over by filename (no OCR) is an open bill until
      // proven paid — file it as an invoice.
      if (handoff.mime.includes('pdf') && !handoff.parsed) setScanDocKind('invoice');
    }
    const parsed = handoff.parsed;
    if (parsed) {
      setScanDocKind(classifyPaidStatus(parsed));
      if (parsed.vendor) {
        setVendor(parsed.vendor);
        const guess = guessExpenseCategory(parsed.vendor);
        if (guess) setCategory(guess);
      }
      // Title = WHAT the money bought, never a vendor echo (Wave 53:
      // the user flagged "title and vendor were the same").
      const kindLabel = classifyPaidStatus(parsed) === 'invoice' ? 'Invoice' : 'Receipt';
      setDescription(
        composeScanTitle(
          parsed,
          kindLabel.toLowerCase(),
          `${kindLabel} — ${new Date(parsed.date ?? Date.now()).toLocaleDateString()}`
        )
      );
      if (parsed.amount_cents != null) setAmount((parsed.amount_cents / 100).toFixed(2));
      if (parsed.date != null) setPaidOn(toLocalYMD(parsed.date));
      // Line items, doc number, terms → Notes, so it's all searchable.
      const scanNotes = composeScanNotes(parsed);
      if (scanNotes) setNotes(scanNotes);
      setScanHint('Smart scan filed this as an expense — check the details, then save.');
    } else {
      setScanHint('Photo attached from Smart scan — fill in the details, then save.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSave = amount.trim().length > 0 && category;

  const runScan = async (source: 'camera' | 'library') => {
    setScanHint(null);
    let asset: ImagePicker.ImagePickerAsset | undefined;
    try {
      if (source === 'camera') {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (!perm.granted) return;
        const res = await ImagePicker.launchCameraAsync({ quality: 0.7, base64: true });
        if (res.canceled || !res.assets[0]) return;
        asset = res.assets[0];
      } else {
        const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (!perm.granted) return;
        const res = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          quality: 0.7,
          base64: true,
        });
        if (res.canceled || !res.assets[0]) return;
        asset = res.assets[0];
      }
    } catch (err) {
      void notice({ title: 'Picker failed', message: String(err), tone: 'error' });
      return;
    }

    const base64 = asset?.base64 ?? null;
    if (!base64 || !asset) {
      void notice({
        title: "Couldn't read photo",
        message: 'Your photo library returned no image data. Try a different file.',
        tone: 'error',
      });
      return;
    }

    setScanning(true);
    try {
      const result = await scanReceiptBase64(base64);
      if (!result.ok) {
        if (result.error.kind === 'no-key') {
          void notice({
            title: 'No Vision API key',
            message: 'Add a Google Vision key in Settings → Admin → API keys to enable receipt scanning.',
            tone: 'warning',
          });
        } else {
          void notice({
            title: result.error.kind === 'network' ? "Couldn't reach Vision" : "Couldn't read receipt",
            message: result.error.message,
            tone: 'error',
          });
        }
        return;
      }
      // Keep the photo regardless of how much parsed — the receipt
      // itself is worth saving even when OCR whiffs on the amount.
      setScanUri(asset.uri);
      applyParsedToForm(result.parsed);
    } finally {
      setScanning(false);
    }
  };

  /** Shared prefill for camera scans and file uploads (Wave 54). */
  const applyParsedToForm = (parsed: ParsedReceipt) => {
    setScanDocKind(classifyPaidStatus(parsed));
    const filled: string[] = [];
    if (parsed.vendor && !vendor) { setVendor(parsed.vendor); filled.push('vendor'); }
    if (parsed.vendor && !categoryTouched) {
      const guess = guessExpenseCategory(parsed.vendor);
      if (guess && guess !== category) {
        setCategory(guess);
        filled.push('category');
      }
    }
    // Title = what the money bought (work summary / top line item),
    // never a vendor echo — vendor has its own field (Wave 53).
    if (!description) {
      setDescription(
        composeScanTitle(
          parsed,
          'receipt',
          `Receipt — ${new Date(parsed.date ?? Date.now()).toLocaleDateString()}`
        )
      );
      filled.push('title');
    }
    if (parsed.amount_cents != null && !amount.trim()) {
      setAmount((parsed.amount_cents / 100).toFixed(2));
      filled.push('amount');
    }
    if (parsed.date != null) {
      setPaidOn(toLocalYMD(parsed.date));
      filled.push('date');
    }
    if (!notes.trim()) {
      const scanNotes = composeScanNotes(parsed);
      if (scanNotes) {
        setNotes(scanNotes);
        filled.push('notes');
      }
    }
    setScanHint(
      filled.length > 0
        ? `Pre-filled: ${filled.join(', ')}. Edit anything that's off, then save.`
        : 'Scan complete — nothing obvious to pre-fill. Check the form.'
    );
  };

  /** Any-file intake (Wave 54): images and PDFs OCR + prefill; other
   *  documents attach so they save with the expense. */
  const runFileScan = async () => {
    setScanHint(null);
    setScanning(true);
    try {
      const res = await pickAndScanFile();
      if (res.status === 'canceled') return;
      if (res.status === 'error') {
        void notice({ title: 'Picker failed', message: res.message, tone: 'error' });
        return;
      }
      setScanUri(res.uri);
      setScanMime(res.mime ?? 'application/octet-stream');
      setScanName(res.name ?? null);
      if (res.parsed) {
        applyParsedToForm(res.parsed);
      } else {
        if (res.mime?.includes('pdf')) setScanDocKind('invoice');
        setScanHint('File attached — it will save with this expense. Fill in the details.');
      }
    } finally {
      setScanning(false);
    }
  };

  const pickScanSource = () => {
    if (Platform.OS === 'web') {
      void runFileScan();
      return;
    }
    void runScan('camera');
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const prop = await getActiveProperty(db);
      if (!prop) {
        void notice({
          title: 'No home set up yet',
          message: 'Finish onboarding first — expenses attach to a home.',
          tone: 'warning',
        });
        return;
      }
      const cents = Math.round(parseFloat(amount.replace(/[^0-9.]/g, '')) * 100);
      if (isNaN(cents)) {
        void notice({
          title: "Couldn't read the amount",
          message: 'Enter a number like 250 or 1,250.00.',
          tone: 'warning',
        });
        return;
      }
      // Local noon, not bare Date.parse — a bare 'YYYY-MM-DD' parses as
      // UTC midnight, which lands on the PREVIOUS local day west of
      // Greenwich. (Wave 45 fix; the quote form had the same bug.)
      const paid = Date.parse(paidOn + 'T12:00:00');
      if (isNaN(paid)) {
        void notice({
          title: "Couldn't read the date",
          message: 'Pick the date again from the calendar.',
          tone: 'warning',
        });
        return;
      }

      // Persist the scanned receipt photo (if any) as a Document BEFORE
      // inserting the expense — slow file I/O stays outside any
      // transaction, and the expense links it via receipt_doc_id so the
      // detail page + Media & Files can show and share it.
      let receiptDocId: string | null = null;
      if (scanUri) {
        try {
          const ext =
            scanUri.match(/\.[a-zA-Z0-9]+$/)?.[0] ??
            (scanMime.includes('pdf') ? '.pdf' : '.jpg');
          const fileName = `receipt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}${ext}`;
          const fileUri = await persistPickedFile(scanUri, fileName);
          const kindLabel = scanDocKind === 'invoice' ? 'Invoice' : 'Receipt';
          // Title carries WHAT the money bought, not just who billed it
          // — "Invoice — site services · Reputation Management" reads in
          // Files without opening the file (Wave 52.1).
          const base = vendor.trim()
            ? `${kindLabel} — ${vendor.trim()}`
            : `${kindLabel} — ${new Date(paid).toLocaleDateString()}`;
          const desc = description.trim();
          const withDesc =
            desc && desc.toLowerCase() !== vendor.trim().toLowerCase()
              ? `${base} · ${desc}`
              : base;
          receiptDocId = await addDocument(db, {
            propertyId: prop.id,
            kind: scanDocKind,
            title: withDesc.slice(0, 80),
            file_uri: fileUri,
            mime_type: scanMime,
            vendor: vendor.trim() || null,
            cost_cents: cents,
            notes: desc || null,
          });
        } catch (err) {
          // A failed photo persist shouldn't lose the expense itself.
          console.warn('receipt doc persist failed', err);
        }
      }

      await addExpense(db, {
        propertyId: prop.id,
        category,
        vendor: vendor.trim() || null,
        amount_cents: cents,
        paid_on: paid,
        description: description.trim() || null,
        recurring,
        tax_deductible: taxDeductible ? 1 : 0,
        receipt_doc_id: receiptDocId,
        notes: notes.trim() || null,
      });
      router.replace('/expenses' as never);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/expenses')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>New expense</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 130 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>Log an expense</Text>
        <Text style={styles.sub}>
          Bills, insurance, taxes, one-off projects. Recurring expenses get
          rolled into your monthly-average estimate.
        </Text>

        <Pressable
          onPress={pickScanSource}
          disabled={scanning}
          style={({ pressed }) => [
            styles.scanBtn,
            pressed && { transform: [{ scale: 0.98 }] },
            scanning && { opacity: 0.6 },
          ]}>
          <ScanLine size={18} color={Palette.forest} strokeWidth={2} />
          <View style={{ flex: 1 }}>
            <Text style={styles.scanTitle}>
              {scanning ? 'Reading receipt…' : scanUri ? 'Re-scan the receipt' : 'Scan a receipt'}
            </Text>
            <Text style={styles.scanSub}>
              {scanning
                ? 'Vision is parsing the text — a couple of seconds.'
                : scanUri
                ? 'File attached — it will be saved with this expense.'
                : Platform.OS === 'web'
                ? 'Photo, PDF, or document — pre-fills the form and keeps the file.'
                : 'Snap a photo — pre-fills the form and keeps the scan.'}
            </Text>
            {Platform.OS !== 'web' && !scanning ? (
              <Pressable onPress={() => void runFileScan()} hitSlop={8}>
                <Text style={styles.scanAltLink}>Or upload a file (PDF, image, doc)</Text>
              </Pressable>
            ) : null}
          </View>
          {/* Attached-file preview (Wave 53): show what will be saved
              with the expense, right where it was attached. */}
          {scanUri && !scanning ? (
            scanMime.includes('pdf') ? (
              <PdfThumb
                uri={scanUri}
                style={styles.scanPreview}
                fallback={
                  <View style={[styles.scanPreview, styles.scanPreviewFallback]}>
                    <ScanLine size={18} color={Palette.forest} strokeWidth={1.6} />
                  </View>
                }
              />
            ) : scanMime.startsWith('image/') ? (
              <Image source={{ uri: scanUri }} style={styles.scanPreview} resizeMode="cover" />
            ) : (
              // docx / spreadsheets / text files: render the extracted
              // text as a paper page. An <Image> with a docx URI drew
              // nothing — the blank white box (Wave 60).
              <DocPageThumb
                uri={scanUri}
                mime={scanMime}
                name={scanName}
                style={styles.scanPreview}
                fallback={
                  <View style={[styles.scanPreview, styles.scanPreviewFallback]}>
                    <ScanLine size={18} color={Palette.forest} strokeWidth={1.6} />
                  </View>
                }
              />
            )
          ) : null}
        </Pressable>
        {scanHint ? (
          <View style={styles.scanHint}>
            <Text style={styles.scanHintText}>{scanHint}</Text>
          </View>
        ) : null}

        <View style={styles.card}>
          <Text style={styles.fieldLabel}>AMOUNT</Text>
          <View style={styles.amountRow}>
            <DollarSign size={18} color={Palette.inkSoft} strokeWidth={2} />
            <TextInput
              value={amount}
              onChangeText={setAmount}
              placeholder="0.00"
              placeholderTextColor={Palette.inkFaint}
              inputMode="decimal"
              style={[styles.amountInput, webNoOutline]}
            />
          </View>

          {/* Title directly under the amount (Wave 53, user request):
              "what was this?" is the second question after "how much?" —
              date/category/vendor are triage details that follow. */}
          <Field label="Title" value={description} onChangeText={setDescription} placeholder="e.g. May electric bill, gutter repair" />

          <View style={{ marginTop: Spacing.three }}>
            <Text style={styles.fieldLabel}>PAID ON</Text>
            <Pressable
              onPress={() => setDateOpen(true)}
              style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }]}>
              <Text style={styles.pickText}>
                {paidOn
                  ? new Date(paidOn + 'T12:00:00').toLocaleDateString(undefined, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : 'Tap to pick'}
              </Text>
              <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
            </Pressable>
          </View>
          <Pick label="Category" value={category} onPress={() => setCatOpen(true)} />
          <Field label="Vendor / payee" value={vendor} onChangeText={setVendor} placeholder="DTE Energy, State Farm, etc." />
        </View>

        <View style={styles.card}>
          <Text style={styles.cardLabel}>RECURRING?</Text>
          <Pick
            label=""
            value={recurring ? RECURRING_OPTIONS.find((o) => o.v === recurring)?.label ?? null : 'One-time'}
            onPress={() => setRecOpen(true)}
          />
          <Pressable onPress={() => setTaxDeductible(!taxDeductible)} style={styles.taxRow}>
            <View style={[styles.taxIcon, taxDeductible && { backgroundColor: Palette.forest }]}>
              <ShieldCheck
                size={16}
                color={taxDeductible ? Palette.cream : Palette.forest}
                strokeWidth={1.8}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.taxLabel}>Tax-deductible</Text>
              <Text style={styles.taxSub}>
                Pulled into a year-end "deductibles" filter for tax prep.
              </Text>
            </View>
            <View style={[styles.toggle, taxDeductible && styles.toggleOn]}>
              <View style={[styles.toggleThumb, taxDeductible && styles.toggleThumbOn]} />
            </View>
          </Pressable>
        </View>

        <View style={styles.card}>
          <Text style={styles.fieldLabel}>NOTES</Text>
          <TextInput
            value={notes}
            onChangeText={setNotes}
            placeholder="Anything to remember"
            placeholderTextColor={Palette.inkFaint}
            multiline
            style={[styles.fieldInput, webNoOutline, { height: 80, textAlignVertical: 'top' }]}
          />
        </View>

      </ScrollView>

      <StickyBottomBar>
        <Pressable
          onPress={save}
          disabled={!canSave || saving}
          style={({ pressed }) => [
            styles.saveBtn,
            { flex: 1 },
            (!canSave || saving) && { opacity: 0.5 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={18} color={Palette.cream} strokeWidth={2} />
          <Text style={styles.saveText}>{saving ? 'Saving…' : 'Log expense'}</Text>
        </Pressable>
      </StickyBottomBar>

      <Modal animationType="fade" transparent visible={catOpen} onRequestClose={() => setCatOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setCatOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Category</Text>
            <ScrollView style={{ maxHeight: 380 }}>
              {allExpenseCategories(customCatsJson).map((c) => {
                const sel = category === c;
                return (
                  <Pressable
                    key={c}
                    onPress={() => {
                      setCategory(c);
                      setCategoryTouched(true);
                      setCatOpen(false);
                    }}
                    style={({ pressed }) => [
                      styles.modalRow,
                      pressed && { backgroundColor: '#EEF3EE' },
                      sel && { backgroundColor: '#EEF3EE' },
                    ]}>
                    <Text style={styles.modalRowText}>{c}</Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal animationType="fade" transparent visible={recOpen} onRequestClose={() => setRecOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setRecOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Recurring?</Text>
            <Pressable
              onPress={() => {
                setRecurring(null);
                setRecOpen(false);
              }}
              style={({ pressed }) => [
                styles.modalRow,
                pressed && { backgroundColor: '#EEF3EE' },
                recurring === null && { backgroundColor: '#EEF3EE' },
              ]}>
              <Text style={styles.modalRowText}>One-time</Text>
            </Pressable>
            {RECURRING_OPTIONS.map((o) => {
              const sel = recurring === o.v;
              return (
                <Pressable
                  key={o.v}
                  onPress={() => {
                    setRecurring(o.v);
                    setRecOpen(false);
                  }}
                  style={({ pressed }) => [
                    styles.modalRow,
                    pressed && { backgroundColor: '#EEF3EE' },
                    sel && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <Text style={styles.modalRowText}>{o.label}</Text>
                </Pressable>
              );
            })}
          </Pressable>
        </Pressable>
      </Modal>

      <DatePickerSheet
        open={dateOpen}
        value={(() => { const t = parseLocalDate(paidOn); return isNaN(t) ? null : t; })()}
        onPick={(ms) => {
          setPaidOn(toLocalYMD(ms));
          setDateOpen(false);
        }}
        onClose={() => setDateOpen(false)}
        title="Paid on"
      />
    </SafeAreaView>
  );
}

function Field({
  label, value, onChangeText, placeholder,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        style={[styles.fieldInput, webNoOutline]}
      />
    </View>
  );
}

function Pick({
  label, value, onPress,
}: {
  label: string;
  value: string | null;
  onPress: () => void;
}) {
  return (
    <View style={{ marginTop: label ? Spacing.three : Spacing.two }}>
      {label ? <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text> : null}
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }]}>
        <Text style={styles.pickText}>{value ?? 'Tap to pick'}</Text>
        <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  card: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  cardLabel: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.6, marginBottom: 4,
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Palette.sand,
    borderRadius: 11,
    borderWidth: 1.5,
    borderColor: Palette.line,
    paddingHorizontal: 12,
    marginTop: 5,
  },
  amountInput: {
    flex: 1,
    paddingVertical: 14,
    paddingLeft: 8,
    fontFamily: Fonts.serifBold,
    fontSize: 28,
    color: Palette.forest,
    letterSpacing: -0.5,
  },
  fieldLabel: {
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  fieldInput: {
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  pickBox: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 12,
  },
  pickText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  taxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: Spacing.three,
    padding: 12,
    backgroundColor: Palette.sand,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  taxIcon: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  taxLabel: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  taxSub: { fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft, marginTop: 1, lineHeight: 16 },
  toggle: {
    width: 40, height: 24, borderRadius: 12,
    backgroundColor: Palette.sandDeep, padding: 3, justifyContent: 'center',
  },
  toggleOn: { backgroundColor: Palette.forest },
  toggleThumb: { width: 18, height: 18, borderRadius: 9, backgroundColor: Palette.cream },
  toggleThumbOn: { transform: [{ translateX: 16 }] },
  saveBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, backgroundColor: Palette.forest,
    paddingVertical: 16, borderRadius: 18,
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 18, width: '100%', maxWidth: 380,
  },
  modalTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink, marginBottom: 10 },
  modalRow: { paddingVertical: 11, paddingHorizontal: 12, borderRadius: 10 },
  modalRowText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  scanBtn: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    flexDirection: 'row', alignItems: 'center', gap: 12,
    flexWrap: 'wrap',
    backgroundColor: '#EAF0EA',
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: 16, padding: 14,
  },
  scanAltLink: {
    marginTop: 6,
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.forest,
    textDecorationLine: 'underline',
  },
  scanPreview: {
    width: '100%', height: 220,
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  scanPreviewFallback: {
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: Palette.sand,
  },
  scanTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  scanSub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    lineHeight: 16,
  },
  scanHint: {
    marginTop: 8, marginHorizontal: Spacing.four,
    padding: 12, borderRadius: 11,
    backgroundColor: 'rgba(225,163,61,0.18)',
    borderWidth: 1, borderColor: 'rgba(225,163,61,0.32)',
  },
  scanHintText: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: '#5A4012', lineHeight: 17,
  },
});
